import { css, Grid, Stack, Typography } from '@mui/material'
import { isNonEmptyArray } from 'effect/Array'
import { useTranslation } from 'next-i18next'
import { useContext, useMemo } from 'react'
import { match } from 'ts-pattern'
import { raceCategoriesLevel1 } from 'helpers/raceCategoryLevels'
import { recordKeys } from 'helpers/records'
import { includesNormalizedString } from 'helpers/string'
import { HomeService } from 'live/Home/HomeService'
import { unifiedColors } from 'styles/colors'
import { AutocompleteResultRow } from './AutocompleteResultRow'
import { EventLink } from './EventLink'
import { SearchBarContext } from './SearchBarContext'
import { SearchTab } from './SearchBarHeader'
import { EventCategory, EventType } from './TypeTabContent'

interface TypeAutocompleteProps {
  close?: () => void
  setSearchTab: (tab: SearchTab) => void
}

const getValidEvents = (events: EventType[]) => {
  return isNonEmptyArray(events) ? events : null
}

const resolveRaces = (
  events: EventType[],
  suggestedEvents: EventType[],
  raceSearch: string
) => {
  if (!raceSearch) {
    return getValidEvents(suggestedEvents) ?? getValidEvents(events)
  }
  return getValidEvents(events)
}

export const TypeAutocomplete = ({
  close,
  setSearchTab
}: TypeAutocompleteProps) => {
  const { t } = useTranslation(['common', 'home'])
  const level1Categories = useMemo(
    () => HomeService.getFeaturedEventsDisplay(t),
    []
  )

  const {
    raceAutocompleteState,
    raceSearch,
    setRaceSearch,
    setRaceType,
    typeAutocomplete: autoComplete
  } = useContext(SearchBarContext)

  const displayedCollections = useMemo(() => {
    if (!raceAutocompleteState) {
      return { types: null, races: null }
    }

    const { editionCategories, events, suggestedEvents } =
      raceAutocompleteState.collections.reduce(
        (acc, { source: { sourceId }, items }) => {
          match(sourceId)
            .with('editionCategories', () => {
              acc.editionCategories = items as EventCategory[]
            })
            .with('events', () => {
              acc.events = items as EventType[]
            })
            .with('suggestedEvents', () => {
              acc.suggestedEvents = items as EventType[]
            })
            .otherwise(() => {})
          return acc
        },
        {
          editionCategories: [] as EventCategory[],
          events: [] as EventType[],
          suggestedEvents: [] as EventType[]
        }
      )

    const types = !!raceSearch
      ? editionCategories
          .filter(({ label }) => includesNormalizedString(label, raceSearch))
          .map(({ key: categoryKey, label }) => {
            const level1Key = recordKeys(raceCategoriesLevel1).find((key) => {
              const level2 = raceCategoriesLevel1[key]
                .level2Categories as readonly string[]
              return key === categoryKey || level2.includes(categoryKey!)
            })

            const icon = level1Categories.find(({ key }) => {
              return key === level1Key
            })?.Icon

            return {
              key: categoryKey,
              label,
              Icon: icon ?? (() => <></>)
            }
          })
      : level1Categories

    return {
      types: types.length > 0 ? types : null,
      races: resolveRaces(events, suggestedEvents, raceSearch)
    }
  }, [raceAutocompleteState?.collections])

  const numberOfColumns =
    displayedCollections.types && displayedCollections.races ? 2 : 1

  const handleSetRaceType = (item: EventCategory | undefined) => {
    autoComplete.setIsOpen(false)
    setSearchTab('date')
    setRaceSearch('')
    if (item) {
      setRaceType({
        key: item?.key,
        label: item?.label,
        facet: level1Categories.some(({ key }) => key.includes(item.key))
          ? 'editionLiveLevel1Category'
          : 'editionLiveLevel2Category'
      })
    }
  }

  if (
    raceAutocompleteState?.isOpen &&
    !isNonEmptyArray(displayedCollections.races || []) &&
    !isNonEmptyArray(displayedCollections.types || [])
  ) {
    return (
      <Stack width="100%" p={2} justifyContent="center" alignItems="center">
        <Typography variant="body2">{t('home:head.noResults')}</Typography>
      </Stack>
    )
  }

  return (
    <Stack className="aa-Autocomplete" {...autoComplete.getRootProps({})}>
      <Grid container spacing={{ md: numberOfColumns }}>
        {raceAutocompleteState?.isOpen ? (
          <>
            {displayedCollections.races && (
              <Grid
                size={{ md: 12 / numberOfColumns }}
                key="events"
                padding={0}
                width="100%"
              >
                <Stack
                  flexDirection="row"
                  padding="12px 16px 8px"
                  gap="13px"
                  alignItems="center"
                  borderRadius="16px"
                >
                  <Typography variant="body2" fontSize={16} fontWeight={600}>
                    {!raceSearch ? t('header.trends') : t('header.races')}
                  </Typography>
                </Stack>
                {displayedCollections.races.map((item) => (
                  <EventLink
                    key={item.objectID}
                    item={item}
                    raceSearch={raceSearch}
                    close={close}
                  />
                ))}
              </Grid>
            )}
            {displayedCollections.types && (
              <Grid
                size={{ md: 12 / numberOfColumns }}
                key="locations"
                width="100%"
                css={css`
                  padding: 0;
                `}
              >
                <Stack
                  flexDirection="row"
                  padding="12px 16px 8px"
                  gap="13px"
                  alignItems="center"
                  borderRadius="16px"
                >
                  <Typography variant="body2" fontSize={16} fontWeight={600}>
                    {t('menu.raceType')}
                  </Typography>
                </Stack>
                {displayedCollections.types.map((item) => (
                  <AutocompleteResultRow
                    key={item.key}
                    background="pink50"
                    iconBackground="pink"
                    Icon={() => (
                      <item.Icon
                        css={css`
                          path {
                            stroke: ${unifiedColors.label.onPink};
                          }
                        `}
                      />
                    )}
                    item={item}
                    setItem={handleSetRaceType}
                    value={item.label}
                  />
                ))}
              </Grid>
            )}
          </>
        ) : null}
      </Grid>
    </Stack>
  )
}
